import { zodResolver } from "@hookform/resolvers/zod";
import { Save, X } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { PtoEntrySelect } from "@/components/pto-entries/PtoEntrySelect";
import { EnumSelect } from "@/components/shared/enum-select";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useRecommendationSource } from "@/hooks/use-recommendation-source";
import {
  ACTIVITY_CATEGORY_OPTIONS,
  isActivityCategory,
  type RecommendationSource,
} from "@/lib/enums";
import {
  type ActivitySuggestionFormValues,
  createActivitySuggestionSchema,
} from "@/schemas/activity-suggestion";
import { GeneratedSuggestionOptions, type PickedSuggestion } from "./GeneratedSuggestionOptions";

interface ActivitySuggestionFormResult {
  title: string;
  note: string | null;
  entryId: string | null;
  category: string | null;
  source?: RecommendationSource;
}

interface ActivitySuggestionFormDefaults {
  title?: string;
  note?: string | null;
  entryId?: string | null;
  entrySelectedLabel?: string;
  category?: string | null;
}

interface ActivitySuggestionFormProps {
  defaultValues?: ActivitySuggestionFormDefaults;
  onSubmit: (result: ActivitySuggestionFormResult) => void;
  onCancel: () => void;
  isSaving: boolean;
  submitLabel?: string;
}

/**
 * One reusable form drives BOTH create and edit — mirrors PtoEntryForm.tsx.
 * The "attach to entry" picker is the only attachment direction built in v1
 * (no reverse picker on PtoEntryForm) — see .claude/rules/frontend.md.
 */
export function ActivitySuggestionForm({
  defaultValues,
  onSubmit,
  onCancel,
  isSaving,
  submitLabel = "Save",
}: ActivitySuggestionFormProps) {
  const form = useForm<ActivitySuggestionFormValues>({
    resolver: zodResolver(createActivitySuggestionSchema),
    defaultValues: {
      title: defaultValues?.title ?? "",
      note: defaultValues?.note ?? "",
      entryId: defaultValues?.entryId ?? null,
      category: (defaultValues?.category as ActivitySuggestionFormValues["category"]) ?? null,
    },
  });

  // EPIC-009 (WI-025): generation fires once both an entry and a category
  // are set, in either order. `picked` is keyed by the entry+category pair
  // it was chosen under, so a stale pick (entry/category changed since)
  // naturally falls back to unset on submit rather than mis-attributing.
  // Gated on the global source being "ai" — in "curated"/Manual mode the
  // whole point is the user fills the form out themselves, so the picker
  // never renders (same query key as ActivitySuggestionCreateDialog's own
  // read, so this doesn't cost an extra request).
  const entryId = form.watch("entryId");
  const category = form.watch("category");
  const { data: recommendationSource } = useRecommendationSource();
  const showGeneratedOptions = Boolean(
    entryId && category && recommendationSource?.source === "ai",
  );
  const generationKey = entryId && category ? `${entryId}-${category}` : null;
  const [picked, setPicked] = useState<{ key: string; source: RecommendationSource } | null>(null);

  function handlePick(suggestion: PickedSuggestion) {
    form.setValue("title", suggestion.title, { shouldDirty: true, shouldValidate: true });
    form.setValue("note", suggestion.note ?? "", { shouldDirty: true });
    if (isActivityCategory(suggestion.category)) {
      form.setValue("category", suggestion.category, { shouldDirty: true });
    }
    if (generationKey) setPicked({ key: generationKey, source: suggestion.source });
  }

  function handleSubmit(values: ActivitySuggestionFormValues) {
    onSubmit({
      title: values.title,
      note: values.note?.trim() ? values.note : null,
      entryId: values.entryId ?? null,
      category: values.category ?? null,
      source: picked?.key === generationKey ? picked.source : undefined,
    });
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Title</FormLabel>
              <FormControl>
                <Input placeholder="Go hiking" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="note"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Note</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Add a note..."
                  className="min-h-[80px]"
                  {...field}
                  value={field.value ?? ""}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="entryId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Attach to a PTO entry (optional)</FormLabel>
              <FormControl>
                <PtoEntrySelect
                  value={field.value ?? null}
                  onChange={(entryId) => field.onChange(entryId)}
                  selectedLabel={defaultValues?.entrySelectedLabel}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="category"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Category (optional — informs AI suggestion generation)</FormLabel>
              <FormControl>
                <EnumSelect
                  value={field.value ?? ""}
                  onValueChange={(next) => field.onChange(next === "" ? null : next)}
                  options={ACTIVITY_CATEGORY_OPTIONS}
                  allOption={{ label: "Not set" }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {showGeneratedOptions && entryId && category && (
          <GeneratedSuggestionOptions
            key={generationKey}
            entryId={entryId}
            category={category}
            onPick={handlePick}
          />
        )}
        <div className="flex gap-2 justify-end">
          <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
            <X className="h-4 w-4 mr-1" />
            Cancel
          </Button>
          <Button type="submit" size="sm" disabled={isSaving}>
            <Save className="h-4 w-4 mr-1" />
            {isSaving ? "Saving…" : submitLabel}
          </Button>
        </div>
      </form>
    </Form>
  );
}
